import api from "./axios";
import type { MerchantDetailsResponse } from "../responses/MerchantDetailsResponse";
import MerchantSearchResponse from "../responses/MerchantSearchResponse";
import type { TopMerchantsResponse } from "../responses/TopMerchantsResponse";
class MerchantService {
  // Tracks in-flight getSearchMerchantsData requests per url so that
  // near-simultaneous callers (e.g. App.vue priming the cache and
  // SearchBar.vue loading retailers on the same page load) share a single
  // network request instead of firing duplicate calls.
  private searchMerchantsInFlight = new Map<string, Promise<MerchantSearchResponse>>();

  async getMerchantDetails(
    applicationName: string,
    clubId: number,
    path: string
  ): Promise<MerchantDetailsResponse> {
    try {
      const url = `/merchant/${applicationName}/${clubId}/${path}`;

      const response = await api.get<MerchantDetailsResponse>(url);
      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        throw new Error(
          "There was an error fetching merchant details: " + err.message
        );
      }
      throw new Error("There was an error fetching merchant details.");
    }
  }

  async getMerchantLink(merchantId: number, offerId: number, merchantName?: string): Promise<string> {
    try {
      const url = `/merchant/${merchantId}/${offerId}/link${merchantName ? `?merchantName=${encodeURIComponent(merchantName)}` : ""}`;

      const response = await api.get<string>(url);

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        throw new Error(
          "There was an error fetching the merchant link: " + err.message
        );
      }
      throw new Error("There was an error fetching the merchant link.");
    }
  }

  async getSearchMerchantsData(
    siteId: string,
    applicationName: string,
    clubId: number
  ): Promise<MerchantSearchResponse> {
    const url = `/merchant/${siteId}/${applicationName}/${clubId}/search`;

    const existing = this.searchMerchantsInFlight.get(url);
    if (existing) {
      return existing;
    }

    const requestPromise = this.fetchSearchMerchantsData(url).finally(() => {
      this.searchMerchantsInFlight.delete(url);
    });

    this.searchMerchantsInFlight.set(url, requestPromise);
    return requestPromise;
  }

  private async fetchSearchMerchantsData(url: string): Promise<MerchantSearchResponse> {
    const CACHE_TTL_MS = 60 * 60 * 1000;
    const cacheName = "merchant-search-cache";
    const timestampKey = `merchantSearchCacheTime:${url}`;

    try {
      // If no window or no Cache API → no caching
      if (typeof window === "undefined" || !("caches" in window)) {
        const response = await api.get<MerchantSearchResponse>(url);
        return response.data;
      }

      const cache = await caches.open(cacheName);
      const cachedResponse = await cache.match(url);
      const cachedTime = Number(localStorage.getItem(timestampKey) || "0");
      const now = Date.now();
      const isFresh = cachedResponse && cachedTime && now - cachedTime < CACHE_TTL_MS;

      // Use cached version if fresh
      if (isFresh && cachedResponse) {
        return (await cachedResponse.json()) as MerchantSearchResponse;
      }

      try {
        // Fetch new data
        const response = await api.get<MerchantSearchResponse>(url);
        const data = response.data;

        // Cache the fresh data
        const blob = new Blob([JSON.stringify(data)], {
          type: "application/json",
        });
        const responseToCache = new Response(blob, {
          headers: { "Content-Type": "application/json" },
        });

        await cache.put(url, responseToCache);
        localStorage.setItem(timestampKey, now.toString());

        return data;
      } catch (netErr) {
        // On network error, fallback to stale cache
        if (cachedResponse) {
          return (await cachedResponse.json()) as MerchantSearchResponse;
        }
        throw netErr;
      }
    } catch (err) {
      if (err instanceof Error) {
        throw new Error(
          "There was an error searching for merchants: " + err.message
        );
      }
      throw new Error("There was an error searching for merchants.");
    }
    }

    async getTopMerchants(topCount: number): Promise<TopMerchantsResponse> {
    try {
        const url = `/merchant/top/${topCount}`;
        const response = await api.get<TopMerchantsResponse>(url);
        return response.data;
    } catch (err) {
        if (err instanceof Error) {
            throw new Error("There was an error fetching merchant details: " + err.message);
        }
        throw new Error("There was an error fetching merchant details.");
    }
}
}

export default new MerchantService();
